import { resolveId } from "@medplum/core";
import type { MedplumClient } from "@medplum/core";

import * as Sentry from "@sentry/react";

import { toast } from "@/utils/notifications";

interface FindRelatedPatientIdParams {
  medplum: MedplumClient;
  patientId: string;
  isOptimistic: boolean | undefined;
}

/**
 * Finds the linked related patient id from Patient.link[0].other.reference.
 * Returns null when no link exists or when optimistic mode suppresses fetch failures.
 */
export async function findRelatedPatientId({
  medplum,
  patientId,
  isOptimistic,
}: FindRelatedPatientIdParams): Promise<string | null> {
  const isOptimisticMode = Boolean(isOptimistic);
  try {
    const patient = await medplum.readResource("Patient", patientId);
    const relatedPatientReference = patient?.link?.[0]?.other?.reference;
    return relatedPatientReference
      ? (resolveId({ reference: relatedPatientReference }) ?? null)
      : null;
  } catch (error) {
    console.warn("Error finding related patient:", error);
    if (!isOptimisticMode) {
      Sentry.captureException(error);
      toast({
        type: "error",
        title: "Could not load related newborn",
        message:
          "The link to the related patient could not be read. Newborn cord or latch live updates may be delayed—try reloading the encounter.",
        autoClose: false,
      });
    }
    return null;
  }
}
